import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { apiGet, TENANT_TOKEN_KEY } from '../../api/client';
import { tokens } from '../../design-system/tokens';
import { OperatorNotificationsBell } from './notifications/OperatorNotificationsBell';
import { ConsoleHelpLink } from './help/ConsoleHelpLink';
import { useHumanAttentionOpenCount } from './useHumanAttentionOpenCount';
import type { HasCalendarResponse } from './calendarView.types';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { readPermissionForAppPath } from '../../auth/permissions';
import { AppVersionLabel } from '../../components/AppVersionLabel';
import { WORKFLOWS_UI_ENABLED } from '../../config/productFeatures';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

/* ─── Icons ─────────────────────────────────────────────────────────────── */

function Icon({ children }: { children: ReactNode }) {
  return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, flexShrink: 0 }}>{children}</span>;
}

function IconHome() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeLinecap="round" strokeLinejoin="round" /></svg></Icon>;
}
function IconAgents() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M9 8V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg></Icon>;
}
function IconConversations() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg></Icon>;
}
function IconChannels() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" /></svg></Icon>;
}
function IconTemplates() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h10M7 16h6" strokeLinecap="round" /></svg></Icon>;
}
function IconLearnings() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg></Icon>;
}
function IconRules() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg></Icon>;
}
function IconHandoff() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" strokeLinecap="round" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" /></svg></Icon>;
}
function IconCalendar() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg></Icon>;
}
function IconPlugins() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg></Icon>;
}
function IconAudit() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" /><polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" /><line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" /><line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" /><polyline points="10 9 9 9 8 9" strokeLinecap="round" /></svg></Icon>;
}
function IconAlerts() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg></Icon>;
}
function IconTest() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></Icon>;
}
function IconAnalytics() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M4 19h16M4 15h10M4 11h16M4 7h12" /></svg></Icon>;
}
function IconWorkflows() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="12" r="2.5" /><circle cx="6" cy="18" r="2.5" /><path d="M8.5 7.5L15.5 11M8.5 16.5L15.5 13" /></svg></Icon>;
}
function IconBlacklist() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg></Icon>;
}
function IconAds() {
  return <Icon><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 20V10M18 20V4M6 20v-4" strokeLinecap="round" strokeLinejoin="round" /></svg></Icon>;
}

/* ─── Nav structure ─────────────────────────────────────────────────────── */

type NavItem = { to: string; label: string; icon: ReactNode; end?: boolean; group: 'general' | 'agent' };

const generalItems: NavItem[] = [
  { to: '/app', label: 'Inicio', icon: <IconHome />, end: true, group: 'general' },
  { to: '/app/agents', label: 'Agentes', icon: <IconAgents />, group: 'general' },
  { to: '/app/message-analytics', label: 'Análisis', icon: <IconAnalytics />, group: 'general' },
];

const agentItemDefs: Array<NavItem & { agentPath?: 'plugins' | 'audit' }> = [
  { to: '/app/conversations', label: 'Conversaciones', icon: <IconConversations />, group: 'agent' },
  { to: '/app/integrations', label: 'Canales', icon: <IconChannels />, group: 'agent' },
  { to: '/app/plantillas', label: 'Plantillas', icon: <IconTemplates />, group: 'agent' },
  { to: '/app/learnings', label: 'Aprendizajes', icon: <IconLearnings />, group: 'agent' },
  { to: '/app/rules', label: 'Reglas', icon: <IconRules />, group: 'agent' },
  { to: '/app/handoff', label: 'Handoff', icon: <IconHandoff />, group: 'agent' },
  { to: '/app/plugins', label: 'Plugins', icon: <IconPlugins />, group: 'agent', agentPath: 'plugins' },
  { to: '/app/audit', label: 'Auditoría', icon: <IconAudit />, group: 'agent', agentPath: 'audit' },
  { to: '/app/settings/notifications', label: 'Alertas', icon: <IconAlerts />, group: 'agent' },
  { to: '/app/settings/brand', label: 'Marca', icon: <IconChannels />, group: 'agent' },
  { to: '/app/test', label: 'Test', icon: <IconTest />, group: 'agent' },
];

const SIDEBAR_WIDTH = 244;

export function ConsoleLayout() {
  const nav = useNavigate();
  const location = useLocation();
  const { can, permissions, session } = useTenantPermissions();
  const canSeeAgentModel = can('web.agents.write');
  const brandLogoSrc = session?.organization?.logoUrl?.trim() || '/logo.png';
  const brandLabel = session?.organization?.name?.trim() || 'Interactive Talker';
  const hasCustomLogo = Boolean(session?.organization?.logoUrl?.trim());
  const [hasCalendar, setHasCalendar] = useState(false);
  const [hasContactBlacklist, setHasContactBlacklist] = useState(false);
  const [hasLeadQualification, setHasLeadQualification] = useState(false);
  const { agents, selectedAgent, selectedAgentId, selectAgent } = useAgentSelector();
  const [selectorOpen, setSelectorOpen] = useState(false);

  /** Al cambiar el selector: el menú Del agente y la pantalla activa siguen a ese agente. */
  const selectAgentAndSyncRoute = (id: string) => {
    if (id === selectedAgentId) {
      setSelectorOpen(false);
      return;
    }
    selectAgent(id);
    const path = location.pathname;
    if (path === '/app/plugins' || path.startsWith('/app/plugins/')) {
      nav(`/app/agents/${id}/plugins`, { replace: true });
      return;
    }
    if (path === '/app/audit' || path.startsWith('/app/audit/')) {
      nav(`/app/agents/${id}/audit`, { replace: true });
      return;
    }
    const m = path.match(/^\/app\/agents\/([^/]+)(\/.*)?$/);
    if (m && m[1] !== id) {
      const rest = m[2] ?? '';
      if (/^\/plugins\/[^/]+/.test(rest)) {
        nav(`/app/agents/${id}/plugins`, { replace: true });
        return;
      }
      if (/^\/calendar\/resources\/[^/]+/.test(rest)) {
        nav(`/app/agents/${id}/calendar/resources`, { replace: true });
        return;
      }
      nav(`/app/agents/${id}${rest}`, { replace: true });
      return;
    }
    if (/^\/app\/conversations\/[^/]+/.test(path)) {
      nav('/app/conversations', { replace: true });
    }
  };

  useEffect(() => {
    if (!localStorage.getItem(TENANT_TOKEN_KEY)) {
      nav('/login', { replace: true });
    }
  }, [nav]);

  useEffect(() => {
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    void (async () => {
      try {
        const me = await apiGet<{ user: { mustChangePassword?: boolean } }>('/api/auth/me', token, { showErrorGrowl: false });
        if (me.user.mustChangePassword) {
          nav('/login/cambiar-contraseña', { replace: true });
        }
      } catch { /* 401 handled by token check */ }
    })();
  }, [nav]);

  useEffect(() => {
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    const qs = selectedAgentId ? `?agentId=${encodeURIComponent(selectedAgentId)}` : '';
    void (async () => {
      try {
        const res = await apiGet<HasCalendarResponse>(`/api/console/calendario/has-calendar${qs}`, token, {
          showErrorGrowl: false,
        });
        setHasCalendar(Boolean(res.hasCalendar));
      } catch {
        setHasCalendar(false);
      }
    })();
    void (async () => {
      try {
        const res = await apiGet<{ hasPlugin: boolean }>(
          `/api/console/contact-blacklist/has-plugin${qs}`,
          token,
          { showErrorGrowl: false },
        );
        setHasContactBlacklist(Boolean(res.hasPlugin));
      } catch {
        setHasContactBlacklist(false);
      }
    })();
    void (async () => {
      try {
        const res = await apiGet<{ hasPlugin: boolean }>(
          `/api/console/lead-qualification/has-plugin${qs}`,
          token,
          { showErrorGrowl: false },
        );
        setHasLeadQualification(Boolean(res.hasPlugin));
      } catch {
        setHasLeadQualification(false);
      }
    })();
  }, [selectedAgentId]);

  const logout = () => {
    localStorage.removeItem(TENANT_TOKEN_KEY);
    nav('/login', { replace: true });
  };

  const effectiveAgentItems = useMemo(() => {
    const items: NavItem[] = agentItemDefs.map((item) => {
      if (item.agentPath && selectedAgentId) {
        return { to: `/app/agents/${selectedAgentId}/${item.agentPath}`, label: item.label, icon: item.icon, group: item.group };
      }
      return { to: item.to, label: item.label, icon: item.icon, end: item.end, group: item.group };
    });
    if (hasCalendar && can('web.calendar.read')) {
      const rulesIdx = items.findIndex((i) => i.label === 'Reglas');
      const calendarItems: NavItem[] = [
        { to: '/app/calendario', label: 'Calendario', icon: <IconCalendar />, group: 'agent' },
      ];
      if (selectedAgentId) {
        calendarItems.push({
          to: `/app/agents/${selectedAgentId}/calendar/bookings`,
          label: 'Reservas',
          icon: <IconCalendar />,
          group: 'agent',
        });
      }
      items.splice(rulesIdx + 1, 0, ...calendarItems);
    }
    if (hasContactBlacklist && can('web.conversations.read')) {
      const convIdx = items.findIndex((i) => i.label === 'Conversaciones');
      items.splice(convIdx + 1, 0, { to: '/app/lista-negra', label: 'Lista negra', icon: <IconBlacklist />, group: 'agent' });
    }
    if (hasLeadQualification && can('web.integrations.read')) {
      const channelsIdx = items.findIndex((i) => i.label === 'Canales');
      items.splice(channelsIdx + 1, 0, { to: '/app/conectores-ads', label: 'Conectores de Ads', icon: <IconAds />, group: 'agent' });
    }
    if (WORKFLOWS_UI_ENABLED) {
      items.splice(0, 0, { to: '/app/workflows', label: 'Flujos', icon: <IconWorkflows />, group: 'agent' });
    }
    return items.filter((item) => {
      const perm = readPermissionForAppPath(item.to);
      return perm ? can(perm) : true;
    });
  }, [hasCalendar, hasContactBlacklist, hasLeadQualification, can, permissions, selectedAgentId]);

  const effectiveGeneralItems = useMemo(() => {
    return generalItems.filter((item) => {
      const perm = readPermissionForAppPath(item.to);
      return perm ? can(perm) : true;
    });
  }, [can, permissions]);

  const { count: convHumanAttentionOpenCount } = useHumanAttentionOpenCount(selectedAgentId);

  if (!localStorage.getItem(TENANT_TOKEN_KEY)) return null;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: tokens.semantic.surfaceSubtle }}>
      {/* ─── Sidebar ─────────────────────────────────── */}
      <aside
        style={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          minHeight: '100vh',
          maxHeight: '100vh',
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${tokens.semantic.border}`,
          overflow: 'hidden',
        }}
        aria-label="Navegación de la consola"
      >
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 16px', borderBottom: `1px solid ${tokens.colors.neutral[100]}` }}>
          <img
            src={brandLogoSrc}
            alt=""
            width={34}
            height={34}
            onError={(e) => {
              const img = e.currentTarget;
              if (img.dataset.fallback === '1') return;
              img.dataset.fallback = '1';
              img.src = '/logo.png';
            }}
            style={{
              display: 'block',
              objectFit: 'contain',
              ...(hasCustomLogo
                ? {}
                : {
                    filter:
                      'drop-shadow(1px 0 0 #7c3aed) drop-shadow(-1px 0 0 #7c3aed) drop-shadow(0 1px 0 #7c3aed) drop-shadow(0 -1px 0 #7c3aed) drop-shadow(1px 1px 0 #7c3aed) drop-shadow(-1px 1px 0 #7c3aed) drop-shadow(1px -1px 0 #7c3aed) drop-shadow(-1px -1px 0 #7c3aed)',
                  }),
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 700, color: tokens.semantic.textPrimary, letterSpacing: '-0.01em' }}>
            {brandLabel}
          </span>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* General group */}
          <div style={groupLabel}>General</div>
          {effectiveGeneralItems.map(({ to, label, icon, end }) => (
            <NavLink key={to} to={to} end={end} style={({ isActive }) => ({ ...linkBase, ...(isActive ? linkActive : {}) })}>
              {icon}
              <span>{label}</span>
            </NavLink>
          ))}

          {/* Agent group */}
          <div style={groupLabel}>Del agente</div>

          {/* Agent selector */}
          <div
            onClick={() => setSelectorOpen(!selectorOpen)}
            style={{
              margin: '2px 0 6px',
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${tokens.colors.primary[100]}`,
              background: tokens.colors.primary[50],
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ width: 24, height: 24, borderRadius: 6, background: tokens.colors.primary[600], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M9 8V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: tokens.colors.primary[600], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedAgent?.name ?? 'Seleccionar agente'}
              </div>
              {canSeeAgentModel ?
                <div style={{ fontSize: 10, color: tokens.semantic.textSubtle }}>
                  {selectedAgent?.model ?? '—'}
                </div>
              : selectedAgent ?
                <div style={{ fontSize: 10, color: tokens.semantic.textSubtle }}>
                  {selectedAgent.isActive ? 'Activo' : 'Pausado'}
                </div>
              : null}
            </div>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={tokens.colors.primary[400]} strokeWidth={2} style={{ flexShrink: 0 }}><polyline points="6 9 12 15 18 9" /></svg>
          </div>

          {/* Agent selector dropdown */}
          {selectorOpen && (
            <div style={{ margin: '0 0 6px', padding: 6, borderRadius: 8, border: `1px solid ${tokens.colors.neutral[200]}`, background: '#fff', boxShadow: '0 8px 24px rgba(15,23,42,0.1)' }}>
              {agents.map((a) => (
                <div
                  key={a.id}
                  onClick={() => { selectAgentAndSyncRoute(a.id); setSelectorOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
                    background: a.id === selectedAgent?.id ? tokens.colors.primary[50] : undefined,
                    opacity: a.isActive ? 1 : 0.5,
                  }}
                >
                  <div style={{ width: 20, height: 20, borderRadius: 5, background: a.id === selectedAgent?.id ? tokens.colors.primary[600] : tokens.colors.neutral[100], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={a.id === selectedAgent?.id ? '#fff' : tokens.colors.neutral[400]} strokeWidth={2.5}><rect x="5" y="8" width="14" height="11" rx="3" /></svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: a.id === selectedAgent?.id ? tokens.colors.primary[600] : tokens.semantic.textPrimary }}>{a.name}</div>
                    <div style={{ fontSize: 10, color: tokens.semantic.textSubtle }}>{a.isActive ? 'Activo' : 'Pausado'}</div>
                  </div>
                  {a.id === selectedAgent?.id && (
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={tokens.colors.primary[600]} strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </div>
              ))}
            </div>
          )}

          {effectiveAgentItems.map(({ to, label, icon }) => {
            const showBell = to === '/app/conversations' && convHumanAttentionOpenCount > 0;
            const auditActive = label === 'Auditoría' && location.pathname.includes('/audit');
            const pluginsActive = label === 'Plugins' && location.pathname.includes('/plugins');
            return (
              <NavLink
                key={`${selectedAgentId ?? 'all'}:${label}`}
                to={to}
                style={({ isActive }) => {
                  const active = isActive || auditActive || pluginsActive;
                  return { ...linkBase, ...(active ? linkActive : {}) };
                }}
              >
                {icon}
                <span style={{ flex: 1 }}>{label}</span>
                {showBell && (
                  <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 8 }}>
                    {convHumanAttentionOpenCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 8px', borderTop: `1px solid ${tokens.colors.neutral[100]}`, flexShrink: 0 }}>
          <AppVersionLabel title="Versión" />
          <button
            type="button"
            onClick={logout}
            title="Cerrar sesión"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '9px 10px',
              marginTop: 8,
              borderRadius: 7,
              border: 'none',
              background: tokens.colors.neutral[50],
              color: tokens.semantic.textMuted,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* ─── Main area ─────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Topbar */}
        <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: '10px 20px', background: '#fff', borderBottom: `1px solid ${tokens.colors.neutral[100]}` }}>
          <ConsoleHelpLink />
          <OperatorNotificationsBell />
        </div>
        {/* Content */}
        <main style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div
            key={selectedAgentId ?? 'all'}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────────────── */

const groupLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: tokens.semantic.textSubtle,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  padding: '14px 10px 5px',
};

const linkBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '8px 10px',
  borderRadius: 7,
  color: tokens.colors.neutral[600],
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
  borderLeft: '3px solid transparent',
  transition: 'background 0.12s ease',
};

const linkActive: CSSProperties = {
  background: tokens.colors.primary[50],
  color: tokens.colors.primary[600],
  fontWeight: 600,
  borderLeftColor: tokens.colors.primary[600],
};
